import { Checkbox, Form, InputNumber, Radio } from 'antd';
import { useContext } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { AVAILABILITY_MODE } from 'app/typings/core/admin/experiences/enum/availabilityMode';
import type { OpeningConfigurationType } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { ExperienceType } from 'app/typings/experiences';

import { useValidationErrors } from '../../../hook/useValidationStore';

import { StockConfigurationTable } from './StockConfigurationTable';

const REQUIRED_RULE = [{ required: true, message: 'Required' }];

type StockConfigurationFieldsProps = {
  isUnlimitedStock: boolean;
  onUnlimitedChange: () => void;
};

export const StockConfigurationFields = ({
  isUnlimitedStock,
  onUnlimitedChange,
}: StockConfigurationFieldsProps) => {
  const { isViewMode, isEditMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance();
  const { stockError } = useValidationErrors();

  const experienceType = Form.useWatch(['experienceType'], form);
  const isOriginal = experienceType === ExperienceType.STAYCATION;
  const openingConfiguration = Form.useWatch(['openingConfiguration'], form);
  const isAlwaysIncludedWithNight = Form.useWatch<boolean>(
    ['isAlwaysIncludedWithNight'],
    form
  );
  const showInInventory = Form.useWatch<boolean>('showInInventory', form);
  const isHiddenFromInventory = showInInventory === false;
  const isTimeslotDaytimeOnly = Form.useWatch<boolean>(
    'isTimeslotDaytimeOnly',
    form
  );
  const availabilityMode = Form.useWatch<AVAILABILITY_MODE>(
    'availabilityMode',
    form
  );
  const isTimeslot = availabilityMode === AVAILABILITY_MODE.TIMESLOT;

  const configurationType: OpeningConfigurationType =
    openingConfiguration?.stockConfiguration?.type;

  return (
    <>
      {!isTimeslot && (
        <FormLineLayout
          isRequired
          label="Configuration"
          labelOnTop
          disabled={isViewMode}
        >
          <div className="stock-configuration-wrapper">
            <Form.Item
              name={['openingConfiguration', 'stockConfiguration', 'type']}
              rules={REQUIRED_RULE}
            >
              <Radio.Group disabled={isViewMode}>
                <Radio
                  value={OPENING_CONFIGURATION_TYPE.CONSTANT}
                  disabled={isHiddenFromInventory}
                >
                  Same every day
                </Radio>
                <Radio
                  value={OPENING_CONFIGURATION_TYPE.DAILY}
                  disabled={isHiddenFromInventory}
                >
                  Depends on the day
                </Radio>
              </Radio.Group>
            </Form.Item>
            {configurationType === OPENING_CONFIGURATION_TYPE.CONSTANT && (
              <>
                <div className="stock-configuration-unlimited-constant">
                  <span className="stock-configuration-unlimited-constant-label">
                    Stock:
                  </span>
                  {isAlwaysIncludedWithNight ? (
                    <span>Publish unlimited (∞) stock everyday</span>
                  ) : (
                    <Checkbox
                      checked={isUnlimitedStock}
                      onChange={onUnlimitedChange}
                      disabled={
                        isViewMode || isOriginal || isHiddenFromInventory
                      }
                    >
                      Unlimited (∞)
                    </Checkbox>
                  )}
                </div>
                {!isAlwaysIncludedWithNight && (
                  <>
                    <div className="stock-configuration-constant">
                      <span>Publish</span>
                      <Form.Item
                        name={[
                          'openingConfiguration',
                          'stockConfiguration',
                          'configuration',
                          'stock',
                        ]}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (isUnlimitedStock) {
                                return Promise.resolve();
                              }

                              if (value === undefined || value === null) {
                                return Promise.reject(new Error('Required'));
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        {isUnlimitedStock ? (
                          <InputNumber disabled placeholder="∞" />
                        ) : isEditMode ? (
                          <InputNumber
                            type="number"
                            min={0}
                            disabled={isHiddenFromInventory}
                          />
                        ) : (
                          <span className="view-data">
                            {openingConfiguration?.stockConfiguration
                              ?.configuration?.stock || 'ø empty'}
                          </span>
                        )}
                      </Form.Item>
                      <span>stock every day</span>
                    </div>
                    {isEditMode && !stockError.isValid && (
                      <span className="form-error-message">
                        {stockError.errors.map((error, index) => (
                          <div key={index}>- {error.message}</div>
                        ))}
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </FormLineLayout>
      )}

      {!isTimeslotDaytimeOnly &&
        configurationType === OPENING_CONFIGURATION_TYPE.DAILY && (
          <FormLineLayout label="Stock" labelOnTop disabled={isViewMode}>
            <StockConfigurationTable />
          </FormLineLayout>
        )}
    </>
  );
};
